/**
 * Mocked conversation data for preview/mocked flows.
 * Used when in mocked mode to show a pre-populated conversation with comments.
 */

import type { ActiveComments } from '@/common/api/models.pleased';

const MOCKED_CONVERSATION_TICKET = {
  id: 123,
  subject: 'Password Reset Issue',
  emailSubject: 'Password Reset Issue - Ticket #123',
  status: 'RESOLVED' as const,
  createdAt: new Date('2024-01-20T10:00:00Z').toISOString(),
  updatedAt: new Date('2024-01-20T10:06:30Z').toISOString(),
};

/**
 * Create mocked conversation data with realistic comments and ticket info.
 * This simulates a resolved/closed conversation with multiple exchanges.
 * Use { minimal: true } for conversation details page (1 message per side + notice).
 */
export const createMockedConversation = (options?: { minimal?: boolean }): ActiveComments => {
  const minimal = options?.minimal ?? false;

  if (minimal) {
    return {
      comments: [
        {
          commentId: 1,
          authorName: 'John Doe',
          authorType: 'CUSTOMER',
          body: "Hello, I need help resetting my password. I tried the forgot password link but I didn't receive any email.",
          bodyHtml:
            "<p>Hello, I need help resetting my password. I tried the forgot password link but I didn't receive any email.</p>",
          source: 'LIVE_CHAT',
          createAt: new Date('2024-01-20T10:00:00Z').toISOString(),
          attachments: undefined,
        },
        {
          commentId: 2,
          authorName: 'Support Agent',
          authorType: 'AGENT',
          body: "Hi John! I'd be happy to help you with that. I've corrected the email and sent a new password reset link. Please check your inbox.",
          bodyHtml:
            "<p>Hi John! I'd be happy to help you with that. I've corrected the email and sent a new password reset link. Please check your inbox.</p>",
          source: 'LIVE_CHAT',
          createAt: new Date('2024-01-20T10:01:30Z').toISOString(),
          attachments: undefined,
        },
        {
          commentId: 3,
          authorName: 'System',
          authorType: 'AGENT',
          body: 'Live chat session ended at 10:06 AM',
          bodyHtml: '<p>Live chat session ended at 10:06 AM</p>',
          source: 'LIVE_CHAT',
          createAt: new Date('2024-01-20T10:06:30Z').toISOString(),
          attachments: undefined,
        },
      ],
      ticket: {
        ...MOCKED_CONVERSATION_TICKET,
        commentCount: 3,
      },
    };
  }

  return {
    comments: [
      {
        commentId: 1,
        authorName: 'John Doe',
        authorType: 'CUSTOMER',
        body: "Hello, I need help resetting my password. I tried the forgot password link but I didn't receive any email.",
        bodyHtml:
          "<p>Hello, I need help resetting my password. I tried the forgot password link but I didn't receive any email.</p>",
        source: 'LIVE_CHAT',
        createAt: new Date('2024-01-20T10:00:00Z').toISOString(),
        attachments: undefined,
      },
      {
        commentId: 2,
        authorName: 'Support Agent',
        authorType: 'AGENT',
        body: "Hi John! I'd be happy to help you with that. Let me check your account details.",
        bodyHtml: "<p>Hi John! I'd be happy to help you with that. Let me check your account details.</p>",
        source: 'LIVE_CHAT',
        createAt: new Date('2024-01-20T10:01:30Z').toISOString(),
        attachments: undefined,
      },
      {
        commentId: 3,
        authorName: 'Support Agent',
        authorType: 'AGENT',
        body: "I can see the issue - the email associated with your account was misspelled. I've corrected it and sent a new password reset link. Please check your email inbox.",
        bodyHtml:
          "<p>I can see the issue - the email associated with your account was misspelled. I've corrected it and sent a new password reset link. Please check your email inbox.</p>",
        source: 'LIVE_CHAT',
        createAt: new Date('2024-01-20T10:03:00Z').toISOString(),
        attachments: undefined,
      },
      {
        commentId: 4,
        authorName: 'John Doe',
        authorType: 'CUSTOMER',
        body: 'Great! I received the email and was able to reset my password successfully. Thank you so much for your help!',
        bodyHtml:
          '<p>Great! I received the email and was able to reset my password successfully. Thank you so much for your help!</p>',
        source: 'LIVE_CHAT',
        createAt: new Date('2024-01-20T10:05:00Z').toISOString(),
        attachments: undefined,
      },
      {
        commentId: 5,
        authorName: 'Support Agent',
        authorType: 'AGENT',
        body: "You're welcome! I'm glad we could resolve this for you. Is there anything else I can help you with today?",
        bodyHtml:
          "<p>You're welcome! I'm glad we could resolve this for you. Is there anything else I can help you with today?</p>",
        source: 'LIVE_CHAT',
        createAt: new Date('2024-01-20T10:05:30Z').toISOString(),
        attachments: undefined,
      },
      {
        commentId: 6,
        authorName: 'John Doe',
        authorType: 'CUSTOMER',
        body: "No, that's all. Thanks again!",
        bodyHtml: "<p>No, that's all. Thanks again!</p>",
        source: 'LIVE_CHAT',
        createAt: new Date('2024-01-20T10:06:00Z').toISOString(),
        attachments: undefined,
      },
      {
        commentId: 7,
        authorName: 'System',
        authorType: 'AGENT',
        body: 'Live chat session ended at 10:06 AM',
        bodyHtml: '<p>Live chat session ended at 10:06 AM</p>',
        source: 'LIVE_CHAT',
        createAt: new Date('2024-01-20T10:06:30Z').toISOString(),
        attachments: undefined,
      },
    ],
    ticket: {
      ...MOCKED_CONVERSATION_TICKET,
      commentCount: 7,
    },
  };
};

/**
 * Create mocked open conversation data for active/ongoing conversations
 * This simulates an open conversation where the user can still interact
 */
export const createMockedOpenConversation = (): ActiveComments => ({
  comments: [
    {
      commentId: 1,
      authorName: 'Jane Smith',
      authorType: 'CUSTOMER',
      body: 'Hi, I have a question about my recent order.',
      bodyHtml: '<p>Hi, I have a question about my recent order.</p>',
      source: 'LIVE_CHAT',
      createAt: new Date('2024-01-23T14:00:00Z').toISOString(),
      attachments: undefined,
    },
    {
      commentId: 2,
      authorName: 'Support Agent',
      authorType: 'AGENT',
      body: "Hello Jane! I'd be happy to help you with your order. Could you please provide me with your order number?",
      bodyHtml:
        "<p>Hello Jane! I'd be happy to help you with your order. Could you please provide me with your order number?</p>",
      source: 'LIVE_CHAT',
      createAt: new Date('2024-01-23T14:01:00Z').toISOString(),
      attachments: undefined,
    },
  ],
  ticket: {
    id: 456,
    subject: 'Order Inquiry',
    emailSubject: 'Order Inquiry - Ticket #456',
    status: 'NEW',
    createdAt: new Date('2024-01-23T14:00:00Z').toISOString(),
    updatedAt: new Date('2024-01-23T14:01:00Z').toISOString(),
    commentCount: 2,
  },
});
